import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import {
  ActionGroup,
  Button,
  Card,
  CardBody,
  CardTitle,
  Content,
  Form,
  FormGroup,
  FormGroupLabelHelp,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Icon,
  Popover,
  TextInput,
} from '@patternfly/react-core';
import { InfoCircleIcon } from '@patternfly/react-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../../api';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUpdateUser } from '../../contexts/user/UserContext';
import { useClient } from '../../hooks/useClient';
import { Client } from '../../types/models/Client';
import { ApiResponseClient } from '../../types/api/ApiResponseClient';
import { CountrySelect } from '@app/utils/CountrySelect';
import { isEqual } from 'lodash';

interface ClientFormData {
  name: string;
  accounting_email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  zipcode: string;
  state: string;
  countrycode: string;
  vat_number: string;
}

function ClientProfileForm() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { client, setClient, loading, error } = useClient();
  const updateUser = useUpdateUser();
  const { addNotification, setDrawerOpen } = useNotifications();

  const clientIDHelpRef = useRef(null);
  const accountingKeyHelpRef = useRef(null);
  const clientAccountingEmailHelpRef = useRef(null);
  const clientNameHelpRef = useRef(null);
  const clientVatNumberHelpRef = useRef(null);
  const clientAddressLine1HelpRef = useRef(null);
  const clientAddressLine2HelpRef = useRef(null);
  const clientCityHelpRef = useRef(null);
  const clientZipcodeHelpRef = useRef(null);
  const clientStateHelpRef = useRef(null);

  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [originalValues, setOriginalValues] = useState<ClientFormData>({
    name: '',
    accounting_email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    zipcode: '',
    state: '',
    countrycode: '',
    vat_number: '',
  });

  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    accounting_email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    zipcode: '',
    state: '',
    countrycode: '',
    vat_number: '',
  });

  // Update form data when client data is loaded
  useEffect(() => {
    if (client) {
      const newValues = {
        name: client.name || '',
        accounting_email: client.accounting_email || '',
        address_line1: client.address_line1 || '',
        address_line2: client.address_line2 || '',
        city: client.city || '',
        zipcode: client.zipcode || '',
        state: client.state || '',
        countrycode: client.countrycode || '',
        vat_number: client.vat_number || '',
      };
      setOriginalValues(newValues);
      setFormData(newValues);
    }
  }, [client]);

  // Check for changes whenever formData is updated
  useEffect(() => {
    const hasChanged = !isEqual(formData, originalValues);
    setIsChanged(hasChanged);
  }, [formData, originalValues, setIsChanged]);

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>, value: string) => {
    const { id } = event.currentTarget;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleCountryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      countrycode: value,
    }));
  };

  const handleAddSuccess = () => {
    addNotification({
      title: 'Organization Profile Update Successful',
      srTitle: 'Success alert',
      description: 'Organization information updated successfully',
      variant: 'success',
    });
    setDrawerOpen(true);
  };

  const handleAddDanger = () => {
    addNotification({
      title: 'Organization Profile Update Error',
      srTitle: 'Error alert',
      description: 'An error occurred while updating organization information',
      variant: 'danger',
    });
    setDrawerOpen(true);
  };

  const handleSubmitCreate = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (!auth.isAuthenticated) {
        throw new Error('Cannot update: not authenticated');
      }

      const response = await apiFetch('/clients', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create organization profile');
      }

      const result: ApiResponseClient<Client> = await response.json();

      if (result.status === 'success' && result.data?.client) {
        setClient(result.data.client);
        // Propagate the new clientID into the user context so that other
        // useClient consumers (Dashboard, Datacenter) pick up the freshly
        // created organization on navigation, without a page reload.
        if (result.data.client.id !== null) {
          updateUser({ clientID: result.data.client.id });
        }
        setOriginalValues(formData);
        setIsChanged(false);
        handleAddSuccess();
      } else {
        throw new Error('Failed to create organization profile');
      }
    } catch (error) {
      handleAddDanger();
      console.error('Error creating organization profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      if (!client?.id || !auth.isAuthenticated) {
        throw new Error('Cannot update: missing client ID or not authenticated');
      }

      const response = await apiFetch('/clients', {
        method: 'PUT',
        headers: { Accept: 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update organization profile');
      }

      const result: ApiResponseClient<Client> = await response.json();

      if (result.status === 'success' && result.data?.client) {
        setClient(result.data.client);
        setOriginalValues(formData);
        setIsChanged(false);
        handleAddSuccess();
      } else {
        throw new Error('Failed to update organization profile');
      }
    } catch (error) {
      handleAddDanger();
      console.error('Error updating organization profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData(originalValues);
    setIsChanged(false);
  };

  // Component code starts here

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  if (loading) {
    return <CenteredSpinner />;
  }

  if (error) {
    return (
      <Content component="p" className="pf-v6-u-text-align-center pf-v6-u-mt-xl">
        Error loading client data: {error.message}
      </Content>
    );
  }

  if (!client) {
    return (
      <>
        <Card isPlain={true}>
          <CardTitle>Immutable Properties</CardTitle>
          <CardBody>
            <Form>
              <FormGroup
                label="Client ID"
                fieldId="clientId"
                labelHelp={
                  <Popover
                    triggerRef={clientIDHelpRef}
                    headerContent={<div>This is your client ID, it is immutable.</div>}
                    bodyContent={<div>Your client ID is linked to your organization and is our primary key.</div>}
                  >
                    <FormGroupLabelHelp ref={clientIDHelpRef} aria-label="More info for client ID field" />
                  </Popover>
                }
              >
                <TextInput id="clientId" isDisabled />
              </FormGroup>
              <FormGroup
                label="Accounting Key"
                fieldId="accountingKey"
                labelHelp={
                  <Popover
                    triggerRef={accountingKeyHelpRef}
                    headerContent={
                      <div>
                        <Icon status="info" className="" style={{ margin: '5px' }}>
                          <InfoCircleIcon />
                        </Icon>
                        This is your client ID, it is immutable.
                      </div>
                    }
                    bodyContent={
                      <div>
                        Your accounting Key is linked to our accounting platform. If this field is empty, it means your
                        organization has never been encoded onto our accounting platform.
                      </div>
                    }
                  >
                    <FormGroupLabelHelp ref={accountingKeyHelpRef} aria-label="More info for accounting key field" />
                  </Popover>
                }
              >
                <TextInput id="accountingKey" isDisabled />
              </FormGroup>
            </Form>
          </CardBody>
        </Card>
        <Card isPlain={true}>
          <CardTitle>Organization Information</CardTitle>
          <CardBody>
            <Form onSubmit={handleSubmitCreate}>
              <FormGroup
                label="Organization Name"
                fieldId="name"
                labelHelp={
                  <Popover
                    triggerRef={clientNameHelpRef}
                    headerContent={<div>Indicate the name of your organization </div>}
                    bodyContent={
                      <div>Your official organization name, or yourself if you&#39;re not part of an organization.</div>
                    }
                  >
                    <FormGroupLabelHelp ref={clientNameHelpRef} aria-label="More info for name field" />
                  </Popover>
                }
              >
                <TextInput id="name" value={formData.name} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Registration (VAT) Number"
                fieldId="vat_number"
                labelHelp={
                  <Popover
                    triggerRef={clientVatNumberHelpRef}
                    headerContent={<div>VAT number </div>}
                    bodyContent={
                      <>
                        <div>Your official organization registration/VAT number.</div>
                        <div>Start with your country code. Example BE0123456789</div>
                      </>
                    }
                  >
                    <FormGroupLabelHelp ref={clientVatNumberHelpRef} aria-label="More info for VAT number field" />
                  </Popover>
                }
              >
                <TextInput id="vat_number" value={formData.vat_number} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Email"
                fieldId="accounting_email"
                labelHelp={
                  <Popover
                    triggerRef={clientAccountingEmailHelpRef}
                    headerContent={<div>Indicate the main email of your organization </div>}
                    bodyContent={<div>Your official organization email. This is a last resort email address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientAccountingEmailHelpRef} aria-label="More info for name field" />
                  </Popover>
                }
              >
                <TextInput id="accounting_email" value={formData.accounting_email} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Address Line 1"
                fieldId="address_line1"
                labelHelp={
                  <Popover
                    triggerRef={clientAddressLine1HelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>First line of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp
                      ref={clientAddressLine1HelpRef}
                      aria-label="More info for address line 1 field"
                    />
                  </Popover>
                }
              >
                <TextInput id="address_line1" value={formData.address_line1} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Address Line 2"
                fieldId="address_line2"
                labelHelp={
                  <Popover
                    triggerRef={clientAddressLine2HelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>Second line of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp
                      ref={clientAddressLine2HelpRef}
                      aria-label="More info for address line 2 field"
                    />
                  </Popover>
                }
              >
                <TextInput id="address_line2" value={formData.address_line2} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="City"
                fieldId="city"
                labelHelp={
                  <Popover
                    triggerRef={clientCityHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>City of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientCityHelpRef} aria-label="More info for city field" />
                  </Popover>
                }
              >
                <TextInput id="city" value={formData.city} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Zip code"
                fieldId="zipcode"
                labelHelp={
                  <Popover
                    triggerRef={clientZipcodeHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>Zip code of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientZipcodeHelpRef} aria-label="More info for zip code field" />
                  </Popover>
                }
              >
                <TextInput id="zipcode" value={formData.zipcode} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="State / Province"
                fieldId="state"
                labelHelp={
                  <Popover
                    triggerRef={clientStateHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>State or Province of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientStateHelpRef} aria-label="More info for state code field" />
                  </Popover>
                }
              >
                <TextInput id="state" value={formData.state} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup label="Country" fieldId="countrycode">
                <CountrySelect
                  value={formData.countrycode}
                  onChange={handleCountryChange}
                  includeEmpty={true}
                  emptyLabel="Please select a country"
                />
              </FormGroup>
              <ActionGroup>
                <Button
                  variant="primary"
                  type="submit"
                  onClick={() => {}}
                  isDisabled={!isChanged || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
                {isChanged && (
                  <Button variant="secondary" type="button" onClick={handleCancel} isDisabled={isSubmitting}>
                    Cancel
                  </Button>
                )}
              </ActionGroup>
            </Form>
          </CardBody>
        </Card>
      </>
    );
  } else {
    return (
      <>
        <Card isPlain={true}>
          <CardTitle>Immutable Properties</CardTitle>
          <CardBody>
            <Form>
              <FormGroup
                label="Client ID"
                fieldId="clientId"
                labelHelp={
                  <Popover
                    triggerRef={clientIDHelpRef}
                    headerContent={<div>This is your client ID, it is immutable.</div>}
                    bodyContent={<div>Your client ID is linked to your organization and is our primary key.</div>}
                  >
                    <FormGroupLabelHelp ref={clientIDHelpRef} aria-label="More info for client ID field" />
                  </Popover>
                }
              >
                <TextInput id="clientId" value={client?.id || ''} isDisabled />
              </FormGroup>
              <FormGroup
                label="VAT Number"
                fieldId="vat_number"
                labelHelp={
                  <Popover
                    triggerRef={clientVatNumberHelpRef}
                    headerContent={<div>VAT number</div>}
                    bodyContent={
                      <>
                        <div>Your official organization registration/VAT number.</div>
                        <div>Start with your country code. Example BE0123456789</div>
                      </>
                    }
                  >
                    <FormGroupLabelHelp ref={clientVatNumberHelpRef} aria-label="More info for VAT number field" />
                  </Popover>
                }
              >
                <TextInput
                  id="vat_number"
                  value={client?.vat_number || ''}
                  aria-describedby={client?.vat_number ? 'vat-validation-status' : undefined}
                  isDisabled
                />
                {client?.vat_number && (
                  <FormHelperText>
                    <HelperText>
                      <HelperTextItem
                        id="vat-validation-status"
                        variant={client.vat_valid ? 'success' : 'warning'}
                      >
                        {client.vat_valid
                          ? 'This VAT number has been validated.'
                          : 'This VAT number has not been validated yet. Our system automatically checks VAT numbers every minute, so this status will update on its own once validation completes.'}
                      </HelperTextItem>
                    </HelperText>
                  </FormHelperText>
                )}
              </FormGroup>
              <FormGroup
                label="Accounting Key"
                fieldId="accountingKey"
                labelHelp={
                  <Popover
                    triggerRef={accountingKeyHelpRef}
                    headerContent={
                      <div>
                        <Icon status="info" className="" style={{ margin: '5px' }}>
                          <InfoCircleIcon />
                        </Icon>
                        This is your Accounting unique ID, it is immutable.
                      </div>
                    }
                    bodyContent={
                      <div>
                        Your accounting Key is linked to our accounting platform. If this field is empty, it means your
                        organization has never been encoded onto our accounting platform.
                      </div>
                    }
                  >
                    <FormGroupLabelHelp ref={accountingKeyHelpRef} aria-label="More info for accounting key field" />
                  </Popover>
                }
              >
                <TextInput id="accountingKey" value={client?.accounting_key || ''} isDisabled />
              </FormGroup>
            </Form>
          </CardBody>
        </Card>
        <Card isPlain={true}>
          <CardTitle>Organization Information</CardTitle>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup
                label="Organization Name"
                fieldId="name"
                labelHelp={
                  <Popover
                    triggerRef={clientNameHelpRef}
                    headerContent={<div>Indicate the name of your organization </div>}
                    bodyContent={
                      <div>Your official organization name, or yourself if you&#39;re not part of an organization.</div>
                    }
                  >
                    <FormGroupLabelHelp ref={clientNameHelpRef} aria-label="More info for name field" />
                  </Popover>
                }
              >
                <TextInput id="name" value={formData.name} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Email"
                fieldId="accounting_email"
                labelHelp={
                  <Popover
                    triggerRef={clientAccountingEmailHelpRef}
                    headerContent={<div>Indicate the main email of your organization </div>}
                    bodyContent={<div>Your official organization email. This is a last resort email address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientAccountingEmailHelpRef} aria-label="More info for name field" />
                  </Popover>
                }
              >
                <TextInput id="accounting_email" value={formData.accounting_email} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Address Line 1"
                fieldId="address_line1"
                labelHelp={
                  <Popover
                    triggerRef={clientAddressLine1HelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>First line of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp
                      ref={clientAddressLine1HelpRef}
                      aria-label="More info for address line 1 field"
                    />
                  </Popover>
                }
              >
                <TextInput id="address_line1" value={formData.address_line1} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Organization Address Line 2"
                fieldId="address_line2"
                labelHelp={
                  <Popover
                    triggerRef={clientAddressLine2HelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>Second line of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp
                      ref={clientAddressLine2HelpRef}
                      aria-label="More info for address line 2 field"
                    />
                  </Popover>
                }
              >
                <TextInput id="address_line2" value={formData.address_line2} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="City"
                fieldId="city"
                labelHelp={
                  <Popover
                    triggerRef={clientCityHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>City of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientCityHelpRef} aria-label="More info for city field" />
                  </Popover>
                }
              >
                <TextInput id="city" value={formData.city} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="Zip code"
                fieldId="zipcode"
                labelHelp={
                  <Popover
                    triggerRef={clientZipcodeHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>Zip code of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientZipcodeHelpRef} aria-label="More info for zip code field" />
                  </Popover>
                }
              >
                <TextInput id="zipcode" value={formData.zipcode} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup
                label="State / Province"
                fieldId="state"
                labelHelp={
                  <Popover
                    triggerRef={clientStateHelpRef}
                    headerContent={<div>First line of Address</div>}
                    bodyContent={<div>State or Province of your organization&#39;s main address.</div>}
                  >
                    <FormGroupLabelHelp ref={clientStateHelpRef} aria-label="More info for state code field" />
                  </Popover>
                }
              >
                <TextInput id="state" value={formData.state} onChange={handleInputChange} />
              </FormGroup>
              <FormGroup label="Country" fieldId="countrycode">
                <CountrySelect
                  value={formData.countrycode}
                  onChange={handleCountryChange}
                  includeEmpty={true}
                  emptyLabel="Please select a country"
                />
              </FormGroup>
              <ActionGroup>
                <Button
                  variant="primary"
                  type="submit"
                  onClick={() => {}}
                  isDisabled={!isChanged || isSubmitting}
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
                {isChanged && (
                  <Button variant="secondary" type="button" onClick={handleCancel} isDisabled={isSubmitting}>
                    Cancel
                  </Button>
                )}
              </ActionGroup>
            </Form>
          </CardBody>
        </Card>
      </>
    );
  }
}

export default ClientProfileForm;
